/**
 * BigcommerceIntegrationViaWebhookPage — Webhook-receiver configuration for the
 * BigCommerce integration partner.
 *
 * Shape mirrors the BE route (`/api/v1/bigcommerce-integration-via-webhook`):
 *  - Shows the per-tenant webhook endpoint URL (copyable).
 *  - Lists subscribable event types (users subscribe/unsubscribe client-side for now —
 *    the BE does not yet persist subscriptions, we keep a local view model).
 *  - Reveals the HMAC signing secret exactly once after rotation.
 *  - Renders a recent-receive feed (event_id / event_type / status) driven by
 *    GET /api/v1/bigcommerce-integration-via-webhook/events.
 *
 * GREEN accent only. No emerald / blue / orange / purple / dark surfaces. No emoji.
 * Partner name "BigCommerce" appears exactly once, in the <h1>.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useBigcommerceIntegrationViaWebhookEvents,
	useBigcommerceIntegrationViaWebhookRotateSecret,
} from "./hooks";
import { getEndpointUrl, type SubscribableEvent } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
// Exported so the unused-locals check does not fail; reserved for future inline inputs.
export const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50";
const KBD =
	"rounded-[4px] border border-zinc-300 bg-zinc-50 px-1.5 py-0.5 font-mono text-xs text-zinc-800";

const DEFAULT_SUBSCRIBABLE_EVENTS: SubscribableEvent[] = [
	{ key: "store/order/created", label: "Order created" },
	{ key: "store/order/updated", label: "Order updated" },
	{ key: "store/customer/created", label: "Customer created" },
	{ key: "store/cart/abandoned", label: "Cart abandoned" },
	{ key: "store/product/updated", label: "Product updated" },
];

function formatTs(sec: number): string {
	if (!Number.isFinite(sec) || sec <= 0) return "-";
	try {
		return new Date(sec * 1000).toISOString().replace("T", " ").slice(0, 19) + "Z";
	} catch {
		return "-";
	}
}

function truncateMid(value: string, head = 12, tail = 6): string {
	if (value.length <= head + tail + 1) return value;
	return `${value.slice(0, head)}.${value.slice(-tail)}`;
}

export function BigcommerceIntegrationViaWebhookPage() {
	const eventsQ = useBigcommerceIntegrationViaWebhookEvents();
	const rotateMut = useBigcommerceIntegrationViaWebhookRotateSecret();

	const endpointUrl = getEndpointUrl();

	const [copied, setCopied] = useState<"url" | "secret" | null>(null);
	const [subscribed, setSubscribed] = useState<Record<string, boolean>>(() => {
		const initial: Record<string, boolean> = {};
		for (const ev of DEFAULT_SUBSCRIBABLE_EVENTS) initial[ev.key] = true;
		return initial;
	});

	const events = eventsQ.data?.events ?? [];
	const isLoading = eventsQ.isPending;
	const isEmpty = !isLoading && events.length === 0;

	const recent = useMemo(() => events.slice(0, 20), [events]);

	const handleCopyUrl = () => {
		if (typeof navigator !== "undefined" && navigator.clipboard) {
			navigator.clipboard.writeText(endpointUrl).catch(() => {});
		}
		setCopied("url");
	};

	const revealedSecret = rotateMut.data?.secret ?? null;

	const handleCopySecret = () => {
		if (!revealedSecret) return;
		if (typeof navigator !== "undefined" && navigator.clipboard) {
			navigator.clipboard.writeText(revealedSecret).catch(() => {});
		}
		setCopied("secret");
	};

	const handleRotate = (e: FormEvent) => {
		e.preventDefault();
		rotateMut.mutate();
	};

	const toggleSubscription = (key: string) => {
		setSubscribed((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	return (
		<main
			data-testid="bigcommerce-integration-via-webhook-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Integration / Webhook
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					BigCommerce Webhook Integration
				</h1>
				<p className="text-sm text-zinc-600">
					Forward store events to Trackerity over a signed HMAC-SHA256 webhook.
					Each tenant has its own endpoint and rotating signing secret.
				</p>
			</header>

			<section className={CARD} aria-label="Webhook endpoint URL">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Endpoint URL
					</h2>
					<button
						type="button"
						className={BTN_GHOST}
						onClick={handleCopyUrl}
						data-testid="bigcommerce-integration-via-webhook-copy-url"
					>
						{copied === "url" ? "Copied" : "Copy"}
					</button>
				</div>
				<p className="mt-2 text-xs text-zinc-600">
					Configure this URL in your store&apos;s webhook destination. Requests
					must include <span className={KBD}>x-tenant-id</span>,{" "}
					<span className={KBD}>x-bigcommerce-integration-via-webhook-timestamp</span>, and{" "}
					<span className={KBD}>x-bigcommerce-integration-via-webhook-signature</span>.
				</p>
				<div
					className="mt-3 break-all rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2 font-mono text-sm text-zinc-800"
					data-testid="bigcommerce-integration-via-webhook-endpoint-url"
				>
					{endpointUrl}
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Subscribed events"
				data-testid="bigcommerce-integration-via-webhook-subscriptions"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Subscribed events
				</h2>
				<p className="mt-1 text-xs text-zinc-600">
					Toggle which event types the store will forward. Subscription state
					is local until you save your store-side webhook configuration.
				</p>
				<ul className="mt-3 divide-y divide-zinc-200">
					{DEFAULT_SUBSCRIBABLE_EVENTS.map((ev) => {
						const on = subscribed[ev.key] === true;
						return (
							<li
								key={ev.key}
								className="flex items-center justify-between gap-4 py-2"
								data-testid={`bigcommerce-integration-via-webhook-event-${ev.key}`}
							>
								<div className="flex flex-col">
									<span className="text-sm font-medium text-zinc-900">
										{ev.label}
									</span>
									<span className="font-mono text-xs text-zinc-500">
										{ev.key}
									</span>
								</div>
								<button
									type="button"
									onClick={() => toggleSubscription(ev.key)}
									className={on ? BTN : BTN_GHOST}
									data-testid={`bigcommerce-integration-via-webhook-toggle-${ev.key}`}
									aria-pressed={on}
								>
									{on ? "Subscribed" : "Unsubscribed"}
								</button>
							</li>
						);
					})}
				</ul>
			</section>

			<section className={CARD} aria-label="Signing secret">
				<h2 className="text-base font-semibold text-zinc-900">
					Signing secret
				</h2>
				<p className="mt-1 text-xs text-zinc-600">
					Rotate the HMAC-SHA256 signing secret. The new value is revealed{" "}
					<span className="font-semibold text-zinc-800">once</span> and is not
					retrievable afterwards.
				</p>
				<form
					onSubmit={handleRotate}
					className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center"
					data-testid="bigcommerce-integration-via-webhook-rotate-form"
				>
					<label className={LABEL} htmlFor="rotate-btn">
						Rotate on demand
					</label>
					<button
						id="rotate-btn"
						type="submit"
						className={BTN}
						disabled={rotateMut.isPending}
						data-testid="bigcommerce-integration-via-webhook-rotate-submit"
					>
						{rotateMut.isPending ? "Rotating." : "Rotate secret"}
					</button>
				</form>

				{rotateMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="bigcommerce-integration-via-webhook-rotate-error"
					>
						{rotateMut.error.message}
					</p>
				) : null}

				{revealedSecret ? (
					<div
						className="mt-3 flex items-center gap-2 rounded-[6px] border border-green-200 bg-green-50 px-3 py-2"
						data-testid="bigcommerce-integration-via-webhook-revealed-secret"
					>
						<code className="flex-1 break-all font-mono text-sm text-zinc-900">
							{revealedSecret}
						</code>
						<button
							type="button"
							className={BTN_GHOST}
							onClick={handleCopySecret}
							data-testid="bigcommerce-integration-via-webhook-copy-secret"
						>
							{copied === "secret" ? "Copied" : "Copy"}
						</button>
					</div>
				) : (
					<p
						className="mt-3 text-xs text-zinc-500"
						data-testid="bigcommerce-integration-via-webhook-secret-placeholder"
					>
						No fresh secret revealed. Rotate above to generate a new one.
					</p>
				)}
			</section>

			<section className={CARD} aria-label="Recent webhook receipts">
				<h2 className="text-base font-semibold text-zinc-900">
					Recent receipts
				</h2>
				<p className="mt-1 text-xs text-zinc-600">
					Last 20 events processed by this endpoint.
				</p>

				{isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="bigcommerce-integration-via-webhook-loading"
					>
						Loading.
					</p>
				) : eventsQ.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="bigcommerce-integration-via-webhook-error"
					>
						{eventsQ.error.message}
					</p>
				) : isEmpty ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="bigcommerce-integration-via-webhook-empty"
					>
						No webhooks received yet.
					</p>
				) : (
					<div
						className="mt-3 overflow-x-auto"
						data-testid="bigcommerce-integration-via-webhook-feed"
					>
						<table className="min-w-full border-collapse text-sm">
							<thead>
								<tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-500">
									<th className="py-2 pr-4 font-medium">Event ID</th>
									<th className="py-2 pr-4 font-medium">Type</th>
									<th className="py-2 pr-4 font-medium">Status</th>
									<th className="py-2 pr-4 font-medium">Received</th>
								</tr>
							</thead>
							<tbody>
								{recent.map((ev) => (
									<tr
										key={ev.id}
										className="border-b border-zinc-100"
										data-testid={`bigcommerce-integration-via-webhook-row-${ev.event_id}`}
									>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-800">
											{truncateMid(ev.event_id)}
										</td>
										<td className="py-2 pr-4 text-xs text-zinc-700">
											{ev.event_type ?? "-"}
										</td>
										<td className="py-2 pr-4 text-xs">
											<span
												className={
													ev.accepted === 1 && ev.sig_valid === 1
														? "rounded-[4px] border border-green-300 bg-green-50 px-1.5 py-0.5 text-green-800"
														: "rounded-[4px] border border-zinc-300 bg-zinc-50 px-1.5 py-0.5 text-zinc-700"
												}
											>
												{ev.accepted === 1 && ev.sig_valid === 1
													? "accepted"
													: "rejected"}
											</span>
										</td>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-600">
											{formatTs(ev.received_at)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default BigcommerceIntegrationViaWebhookPage;
